if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.productInfoId = this.dataset.productInfoId;
        this.button = this.querySelector('[data-sticky-submit]');
        this.buttonLabel = this.querySelector('[data-sticky-submit-label]');
        this.price = this.querySelector('[data-sticky-price]');
        this.variant = this.querySelector('[data-sticky-variant]');
        this.syncFrame = null;
        this.isSubmitting = false;
      }

      connectedCallback() {
        this.productInfo = document.getElementById(this.productInfoId);
        this.sourceButton = this.getSourceButton();
        if (!this.productInfo || !this.button || !this.sourceButton) return;

        this.button.addEventListener('click', this.handleSubmit);
        this.observeSourceButton();
        this.mutationObserver = new MutationObserver(() => this.queueSync());
        this.mutationObserver.observe(this.productInfo, {
          attributes: true,
          childList: true,
          characterData: true,
          subtree: true,
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        }

        this.resizeObserver = new ResizeObserver(() => this.updateReservedSpace());
        this.resizeObserver.observe(this);
        this.previewObserver = new MutationObserver(() => this.observePreviewBar());
        this.previewObserver.observe(document.body, { childList: true });
        window.addEventListener('resize', this.updatePreviewOffset);
        this.observePreviewBar();
        this.updateReservedSpace();
        this.syncFromSource();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.handleSubmit);
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.previewObserver?.disconnect();
        this.previewResizeObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        window.removeEventListener('resize', this.updatePreviewOffset);
        if (this.syncFrame) window.cancelAnimationFrame(this.syncFrame);
        document.body.classList.remove('yn-sticky-cart-visible');
        document.documentElement.style.removeProperty('--yn-sticky-cart-height');
        document.documentElement.style.removeProperty('--yn-sticky-cart-preview-offset');
      }

      getSourceButton() {
        return this.productInfo?.querySelector(`#ProductSubmitButton-${CSS.escape(this.sectionId)}`);
      }

      observeSourceButton() {
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            const hasPassedButton = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
            this.toggleVisibility(hasPassedButton);
          },
          { threshold: 0.01 }
        );
        this.intersectionObserver.observe(this.sourceButton);
      }

      toggleVisibility(visible) {
        this.dataset.visible = String(visible);
        this.setAttribute('aria-hidden', String(!visible));
        document.body.classList.toggle('yn-sticky-cart-visible', visible);
      }

      handleSubmit = () => {
        const sourceButton = this.getSourceButton();
        if (!sourceButton || this.isUnavailable(sourceButton) || this.isSubmitting) return;

        this.isSubmitting = true;
        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');
        sourceButton.click();
        this.queueSync();
      };

      handleVariantChange = ({ data }) => {
        if (String(data?.sectionId) !== String(this.sectionId)) return;

        const title = data.variant?.title === 'Default Title' ? '' : data.variant?.title || '';
        this.variant.textContent = title;
        this.variant.classList.toggle('hidden', title === '');
        this.queueSync();
      };

      queueSync() {
        if (this.syncFrame) return;
        this.syncFrame = window.requestAnimationFrame(() => {
          this.syncFrame = null;
          this.syncFromSource();
        });
      }

      syncFromSource() {
        const latestSourceButton = this.getSourceButton();
        if (!latestSourceButton) {
          this.toggleVisibility(false);
          return;
        }

        if (latestSourceButton !== this.sourceButton) {
          this.sourceButton = latestSourceButton;
          this.observeSourceButton();
        }

        const unavailable = this.isUnavailable(this.sourceButton);
        const sourceLabel = this.sourceButton.querySelector(':scope > span')?.textContent.trim();
        const sourceIsLoading = this.sourceButton.classList.contains('loading');

        this.button.disabled = unavailable;
        this.button.toggleAttribute('aria-busy', sourceIsLoading);
        this.isSubmitting = sourceIsLoading;
        if (sourceLabel) this.buttonLabel.textContent = sourceLabel;

        const priceText = this.getCurrentPrice();
        this.price.classList.toggle('hidden', !priceText);
        if (priceText) this.price.textContent = priceText;
      }

      isUnavailable(button) {
        return button.disabled || button.getAttribute('aria-disabled') === 'true';
      }

      getCurrentPrice() {
        const priceContainer = this.productInfo.querySelector(`#price-${CSS.escape(this.sectionId)}`);
        if (!priceContainer || priceContainer.classList.contains('hidden')) return '';

        const priceRoot = priceContainer.querySelector('.price');
        const selector = priceRoot?.classList.contains('price--on-sale')
          ? '.price__sale .price-item--sale'
          : '.price__regular .price-item--regular';
        return priceContainer.querySelector(selector)?.textContent.trim() || '';
      }

      updateReservedSpace() {
        const height = Math.ceil(this.getBoundingClientRect().height);
        if (!height) return;
        document.documentElement.style.setProperty('--yn-sticky-cart-height', `${height}px`);
      }

      observePreviewBar() {
        // New Shopify previews use a full-screen iframe inside a clipped, resizable wrapper.
        const previewBar = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
        if (previewBar === this.previewBar) {
          this.updatePreviewOffset();
          return;
        }

        this.previewResizeObserver?.disconnect();
        this.previewBar = previewBar;
        if (this.previewBar) {
          this.previewResizeObserver = new ResizeObserver(this.updatePreviewOffset);
          this.previewResizeObserver.observe(this.previewBar);
        }
        this.updatePreviewOffset();
      }

      updatePreviewOffset = () => {
        const rect = this.previewBar?.getBoundingClientRect();
        const touchesViewportBottom = rect && rect.height > 0 && rect.bottom >= window.innerHeight - 2;
        const offset = touchesViewportBottom ? Math.ceil(window.innerHeight - rect.top) : 0;
        document.documentElement.style.setProperty('--yn-sticky-cart-preview-offset', `${Math.max(0, offset)}px`);
      };
    }
  );
}
